import React, { useState, useRef, useEffect } from "react";
import {
  GoogleMap,
  // LoadScript,
  useJsApiLoader,
  Marker,
  Autocomplete,
} from "@react-google-maps/api";
import { useDispatch, useSelector } from "react-redux";
import { App, Flex, Card, Typography, Button, Modal, Tooltip, Drawer, Grid} from "antd";
import {
  fetchBranchLocation,
  saveBranchLocation,
  deleteBranchLocation,
} from "./branchLocationThunk";
import { GOOGLE_MAP_LIBRARIES } from "../../constants/googleMaps";
import { ArrowLeftOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;

const containerStyle = { width: "100%", height: "calc(100vh - 140px)" };
const defaultCenter = { lat: 25.19659, lng: 55.3050064 };

// const libraries = ["places"];
const libraries = GOOGLE_MAP_LIBRARIES;

const BranchLocation = ({ authInfo }) => {
  const mapApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const { modal } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { location } = useSelector((state) => state.branchLocation);

  const [marker, setMarker] = useState(null);
  const [map, setMap] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const autocompleteRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: mapApiKey,
    libraries:  GOOGLE_MAP_LIBRARIES,
  });


  // load saved location
  useEffect(() => {
    if (authInfo?.userInfo?.branch_id) {
      dispatch(fetchBranchLocation(authInfo.userInfo.branch_id));
    }
  }, [authInfo, dispatch]);

  // update marker when store changes
  useEffect(() => {
    setMarker(location);
  }, [location]);

  // Cleanup on unmount (prevents edge crashes)
  useEffect(() => {
    return () => {
      setMap(null);
      autocompleteRef.current = null;
    };
  }, []);


  // handle map click (new marker)
  const handleMapClick = (event) => {
    if (marker && marker.latitude && marker.longitude) {
      Modal.warning({
        title: "Location Limit",
        content:
          "Only one location is allowed. To change location, remove the previous marker first by clicking on it.",
      });
      return;
    }

    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    const newMarker = { latitude: lat, longitude: lng, name: "Branch Location" };

    Modal.confirm({
      title: "Set this as location?",
      content: "Do you want to save this as your branch location?",
      okText: "Yes, Save",
      cancelText: "Cancel",
      onOk: () => {
        setMarker(newMarker);
        dispatch(
          saveBranchLocation({ branch_id: authInfo.userInfo.branch_id, ...newMarker })
        );
      },
    });
  };

  // delete location
  const handleDelete = () => {
    Modal.confirm({
      title: "Remove Location?",
      content: "Do you want to delete this branch location?",
      okText: "Yes, Remove",
      cancelText: "Cancel",
      onOk: () => {
        dispatch(deleteBranchLocation(authInfo.userInfo.branch_id));
        setMarker(null);
      },
    });
  };

  // autocomplete search
  const onPlaceChanged = () => {
    if (!autocompleteRef.current || !window.google) return;
    
      const place = autocompleteRef.current.getPlace();
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const newMarker = {
          latitude: lat,
          longitude: lng,
          name: place.formatted_address || "Selected Location",
        };

        if (map) {
          map.panTo({ lat, lng });
          map.setZoom(15);
        }

        Modal.confirm({
          title: "Use this location?",
          content: place.formatted_address,
          onOk: () => {
            setMarker(newMarker);
            dispatch(
              saveBranchLocation({ branch_id: authInfo.userInfo.branch_id, ...newMarker })
            );
          },
        });
      }
    
  };

  // drag marker → save immediately
  const handleDragEnd = (e) => {
    const newMarker = {
      latitude: e.latLng.lat(),
      longitude: e.latLng.lng(),
      name: "Branch Location",
    };
    setMarker(newMarker);
    dispatch(saveBranchLocation({ branch_id: authInfo.userInfo.branch_id, ...newMarker }));
  };

  return (
    <div style={{ position: "relative" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <Button
          type="text"
          icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
          onClick={() => navigate(-1)}
          data-testid="branch-location-back-button"
        />
        <Title level={3} style={{ margin: 0 }}>
          Branch Location
        </Title>

        <div style={{ marginLeft: "auto" }}>
          <Tooltip title="Help">
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
              onClick={() => setHelpOpen(true)}
            />
          </Tooltip>
        </div>
      </div>
      {/* <LoadScript
        googleMapsApiKey={mapApiKey}
        libraries={libraries}
      > */}
      {isLoaded && (
        <>
        {/* Search Box */}
        <Flex justify="center" style={{ marginBottom: 10 }}>
          <Autocomplete onLoad={(a) => (autocompleteRef.current = a)} onPlaceChanged={onPlaceChanged}>
            <input
              type="text"
              placeholder="Search location..."
              style={{ width: "300px", height: "40px", padding: "8px" }}
            />
          </Autocomplete>
        </Flex>

        {/* Map */}
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={
            marker &&
            marker.latitude &&
            marker.longitude &&
            !isNaN(parseFloat(marker.latitude)) &&
            !isNaN(parseFloat(marker.longitude))
              ? { lat: parseFloat(marker.latitude), lng: parseFloat(marker.longitude) }
              : defaultCenter
          }
          zoom={
            marker &&
            marker.latitude &&
            marker.longitude &&
            !isNaN(parseFloat(marker.latitude))
              ? 15
              : 5
          }
          onClick={handleMapClick}
          onLoad={(mapInstance) => setMap(mapInstance)}
        >
          {marker && (
            <Marker
              position={{ lat: parseFloat(marker.latitude), lng: parseFloat(marker.longitude) }}
              draggable
              onDragEnd={handleDragEnd}
              onClick={handleDelete}
            />
          )}
        </GoogleMap>
        </>
      )}
      <Drawer
        title="Branch Location Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={430}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            Branch Location Management
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "8px",
            }}
          >
            The Branch Location module is used to manage and store the geographic
            location of company branches using Google Maps integration within the ERP system.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Available Actions</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>Search locations using Google Maps autocomplete</li>
              <li>Set branch location directly from the map</li>
              <li>Save branch geographic coordinates</li>
              <li>Drag and reposition branch markers</li>
              <li>Delete saved branch locations</li>
              <li>View saved latitude and longitude details</li>
              <li>Manage one branch location at a time</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Only one saved location is allowed per branch.</li>
              <li>Dragging the marker updates the location immediately.</li>
              <li>Clicking the marker allows removal of the saved location.</li>
              <li>Google Maps API integration is required for functionality.</li>
              <li>Saved branch locations help improve branch tracking and operations.</li>
            </ul>
          </div>
        </div>
      </Drawer>
      {/* </LoadScript> */}

      {/* Info Card */}
      {marker && (
        <Card
          style={{
            position: "absolute",
            bottom: "20px",
            left: "20px",
            maxWidth: 350,
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            backgroundColor: "#fff",
            zIndex: 10,
          }}
        >
          <Title level={5}>Saved Branch Location</Title>
          <Text strong>Branch:</Text> {authInfo?.userInfo?.branch_name || "N/A"}
          <br />
          <Text strong>Latitude:</Text> {marker.latitude}
          <br />
          <Text strong>Longitude:</Text> {marker.longitude}
          <br />
          <Button danger block style={{ marginTop: 8 }} onClick={handleDelete}>
            Remove Location
          </Button>
        </Card>
      )}
    </div>
  );
};

export default BranchLocation;
